"use client";

import { useSeriesBookmarks } from "@/hooks/use-bookmarks";
import { useSession } from "@/lib/auth/client";
import { getFormattedImageUrl, getFormattedViewCounts } from "@/lib/utils";
import type { SeriesWithVideoTrainer } from "@/types";
import { Heart, Zap } from "lucide-react";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import ChessTitle, { parseChessTitle } from "./chess-title";
import RatingDisplay from "./rating-display";
import { Badge } from "./shadcn/badge";
import { Card, CardContent, CardDescription, CardTitle } from "./shadcn/card";

const BookmarkButton = dynamic(() => import("./bookmark-button"), {
    ssr: false,
});

type SeriesCardSeries = SeriesWithVideoTrainer & {
    _count?: {
        Videos?: number;
    };
    viewCount?: number;
};

interface SeriesCardProps {
    series: SeriesCardSeries;
    variant: "training" | "entertainment";
    href?: string;
    imageSrc?: string | null;
}

export default function SeriesCard({
    series,
    variant,
    href,
    imageSrc,
}: SeriesCardProps) {
    const t = useTranslations("series");
    const { data: session } = useSession();
    const { isBooked, toggleBookmark } = useSeriesBookmarks();
    const isEntertainment = variant === "entertainment";
    const seriesHref =
        href ??
        (isEntertainment
            ? `/seri/eglence/${series.Slug}`
            : `/seri/egitimler/${series.Slug}`);
    const trainerName = `${series.VideoTrainer.FirstName} ${series.VideoTrainer.Lastname}`;
    const metadataText = isEntertainment
        ? series.Season
        : series.SeriRatingMin && series.SeriRatingMax
          ? `${series.SeriRatingMin}-${series.SeriRatingMax}`
          : null;
    const videoCount = series._count?.Videos;
    const viewCount = series.viewCount;
    const trainerTitle = parseChessTitle(series.VideoTrainer.Title);
    const resolvedImageSrc =
        getFormattedImageUrl(imageSrc ?? series.ImageUrl) ||
        "/images/series-ph.jpg";
    const isRemoteImage = /^https?:\/\//.test(resolvedImageSrc);

    return (
        <div className="relative h-full min-w-0">
            <Link href={seriesHref} className="block h-full min-w-0">
                <Card className="relative h-full min-w-0 gap-0 overflow-hidden p-0">
                    <div className="relative aspect-6/5 w-full shrink-0 overflow-hidden">
                        <Image
                            src={resolvedImageSrc}
                            alt={t("imageAlt", { title: series.Title })}
                            fill
                            unoptimized={isRemoteImage}
                            sizes="(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                            className="object-cover"
                        />
                        {typeof videoCount === "number" && videoCount > 0 && (
                            <Badge className="absolute bottom-2 left-4">
                                {videoCount}{" "}
                                {isEntertainment
                                    ? t("episodesShort")
                                    : t("videosShort")}
                            </Badge>
                        )}
                        {!isEntertainment && typeof viewCount === "number" && (
                            <Badge
                                variant="secondary"
                                className="absolute right-4 bottom-2"
                            >
                                {getFormattedViewCounts(viewCount)} {t("views")}
                            </Badge>
                        )}
                    </div>

                    <CardContent className="flex flex-col gap-2 p-4">
                        <CardTitle className="line-clamp-2 min-h-[2lh]">
                            {series.Title}
                        </CardTitle>
                        <CardDescription className="flex min-w-0 items-center gap-1">
                            {trainerTitle && (
                                <ChessTitle title={trainerTitle} />
                            )}
                            <span className="truncate">{trainerName}</span>
                        </CardDescription>
                        <div className="flex flex-wrap justify-between gap-2">
                            <RatingDisplay
                                rating={series.averageRating}
                                ratingCount={series.ratingCount}
                            />
                            {metadataText && (
                                <Badge
                                    variant={
                                        isEntertainment
                                            ? "default"
                                            : "secondary"
                                    }
                                >
                                    {!isEntertainment && (
                                        <Zap
                                            data-icon="inline-start"
                                            aria-hidden="true"
                                        />
                                    )}
                                    {metadataText}
                                </Badge>
                            )}
                        </div>
                    </CardContent>

                    {series.Isnew === 1 && (
                        <Badge className="bg-new text-new-foreground absolute top-4 left-4">
                            {t("isNew")}
                        </Badge>
                    )}
                </Card>
            </Link>

            {session?.user && (
                <BookmarkButton
                    handler={async () => {
                        await toggleBookmark(series.Id);
                    }}
                    isBooked={isBooked(series.Id)}
                    icon={Heart}
                    className="absolute top-4 right-4 z-10"
                />
            )}
        </div>
    );
}
